import { z } from 'zod';

import { astDocumentSchema } from './blocks';
import { variablesSchemaSchema } from './variables';

export const templateStatusSchema = z.enum(['draft', 'published', 'archived']);
export type TemplateStatus = z.infer<typeof templateStatusSchema>;

export const templateSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  collectionId: z.string().uuid().nullable(),
  status: templateStatusSchema,
  latestVersionNumber: z.number().int().nonnegative(),
  updatedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  sendsLast30Days: z.number().int().nonnegative().default(0),
  openRate: z.number().min(0).max(1).nullable().default(null),
});

export const templateSchema = templateSummarySchema.extend({
  description: z.string().nullable(),
  tags: z.array(z.string()).default([]),
});

export const templateVersionSchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  versionNumber: z.number().int().positive(),
  isPublished: z.boolean(),
  astJson: astDocumentSchema,
  jsxSource: z.string(),
  variablesSchema: variablesSchemaSchema,
  changeSummary: z.string().nullable(),
  createdAt: z.string().datetime(),
  createdBy: z.string().uuid(),
});

export const createTemplateSchema = z.object({
  name: z.string().min(1).max(120),
  collectionId: z.string().uuid().nullable().optional(),
  forkFromGalleryId: z.string().uuid().optional(),
});

export const trackingDefaultsSchema = z.object({
  open: z.boolean().default(true),
  click: z.boolean().default(true),
});

export const updateTemplateSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(1000).nullable().optional(),
  collectionId: z.string().uuid().nullable().optional(),
  tags: z.array(z.string()).optional(),
  defaultTracking: trackingDefaultsSchema.nullable().optional(),
});

export type TrackingDefaults = z.infer<typeof trackingDefaultsSchema>;

export const saveVersionSchema = z.object({
  astJson: astDocumentSchema,
  variablesSchema: variablesSchemaSchema,
  changeSummary: z.string().max(280).optional(),
});

export const publishVersionSchema = z.object({
  versionId: z.string().uuid(),
});

export type Template = z.infer<typeof templateSchema>;
export type TemplateSummary = z.infer<typeof templateSummarySchema>;
export type TemplateVersion = z.infer<typeof templateVersionSchema>;
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>;
export type SaveVersionInput = z.infer<typeof saveVersionSchema>;
